import { useState, useEffect } from 'react';
import { SoundArrayType } from '../utils/types';
import { DEFAULT_MESSAGE } from '../utils/constants';

type RecorderResponseType = {
  started: boolean;
  errorType?:
    | 'micPermissionDismissedError'
    | 'micPermissionDeniedError'
    | 'recorderError'
    | 'audioSupportError'
    | '';
  error?: string;
};

const useRecorder = () => {
  const [audioChunks, setAudioChunks] = useState<any>([]);
  const [audio, setAudio] = useState<any>([]);
  const [recorder, setRecorder] = useState<any>(null);
  const [silenceDetected, setSilenceDetected] = useState<boolean>(false);
  const [userSpeaking, setUserSpleaking] = useState<boolean>(false);
  const [mimeType, setMimeType] = useState<string>('');
  const mimeTypes = [
    'audio/webm', //android
    'audio/wav',
    'audio/ogg',
    'audio/mpeg',
    'audio/mp4', //ios
    'audio/aac',
    'audio/flac',
    'audio/m4a',
    'audio/x-matroska',
    'audio/x-wav',
    'audio/x-aac',
    'audio/x-caf',
    'audio/x-flac',
  ];

  const soundArrayLength = 4;
  const getMimeType = () => {
    for (let i = 0; i < mimeTypes.length; i++) {
      if (MediaRecorder.isTypeSupported(mimeTypes[i])) {
        return mimeTypes[i];
      }
    }
    return '';
  };

  useEffect(() => {
    const mime = getMimeType();
    setMimeType(mime);
  }, []);

  const startRecording = async (): Promise<RecorderResponseType> => {
    if (!mimeType || !('MediaRecorder' in window)) {
      return {
        started: false,
        errorType: 'audioSupportError',
        error: DEFAULT_MESSAGE.audioSupportErr,
      };
    }

    let streamData: MediaStream;
    let mediaRecorder: MediaRecorder;
    try {
      streamData = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      mediaRecorder = new MediaRecorder(streamData, {
        mimeType,
      } as MediaRecorderOptions);

      // setStream(streamData);
      setRecorder(mediaRecorder);
    } catch (e: any) {
      console.log(e?.message);
      if (e?.message === 'Permission denied') {
        return {
          started: false,
          errorType: 'micPermissionDeniedError',
          error: DEFAULT_MESSAGE.micPermissionErr,
        };
      } else if (e?.message === 'Permission dismissed') {
        return {
          started: false,
          errorType: 'micPermissionDismissedError',
          error: DEFAULT_MESSAGE.micPermissionErr,
        };
      } else {
        return {
          started: false,
          errorType: 'recorderError',
          error: DEFAULT_MESSAGE.recorderErr,
        };
      }
    }

    setSilenceDetected(false);
    try {
      mediaRecorder.start(1000); //Fix Whisper has problem with audio/mp4 blobs from Safari
    } catch (e) {
      console.log(e);
      return {
        started: false,
        errorType: 'recorderError',
        error: DEFAULT_MESSAGE.recorderErr,
      };
    }

    let _audioChunks: any[] = [];
    mediaRecorder.ondataavailable = (event: any) => {
      if (typeof event.data === 'undefined') return;
      if (event.data.size === 0) return;
      _audioChunks.push(event.data);
    };
    setAudioChunks(_audioChunks);

    const context = new AudioContext();
    const source = context.createMediaStreamSource(streamData);
    const analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Float32Array(bufferLength);

    let soundArray: SoundArrayType[] = [];
    let intervalId: any = null;
    let soundRecorded = false;

    const detectSilence = () => {
      if (
        (soundArray.length === soundArrayLength &&
          soundArray.every((val) => val === 0) &&
          soundRecorded) ||
        mediaRecorder.state === 'inactive'
      ) {
        setSilenceDetected(true);
        soundRecorded = false;
        clearInterval(intervalId);
        return;
      }
      if (soundArray.length >= soundArrayLength) {
        soundArray = [];
      }
      analyser.getFloatTimeDomainData(dataArray);
      const rms = Math.sqrt(
        Array.from(dataArray).reduce((acc, val) => acc + val ** 2, 0) /
          bufferLength
      );
      console.log(rms);
      if (rms < 0.01) {
        console.log('-------');
        setUserSpleaking(false);
        soundArray.push(0);
      } else {
        console.log('|||||||');
        setUserSpleaking(true);
        soundArray.push(1);
        soundRecorded = true;
      }
    };

    intervalId = setInterval(detectSilence, 400);

    return { started: true };
  };

  const stopRecording = (callback: (blob: any, url: string) => void) => {
    recorder.stop();
    recorder.onstop = () => {
      const audioBlob = new Blob(audioChunks, { type: mimeType });
      const audioUrl = URL.createObjectURL(audioBlob);

      setAudio(audio.concat(audioUrl));
      setAudioChunks([]);
      callback(audioBlob, audioUrl);
    };
  };

  return {
    startRecording,
    stopRecording,
    recorder,
    audio,
    silenceDetected,
    userSpeaking,
  };
};

export default useRecorder;
